import { cached } from '@porotutu/react'
import {
  historyDeps,
  historySearchOf,
  historyWindow,
} from '@porotutu/react/components/History/History'
import type { Writable } from '@porotutu/react/components/RecordTrail/RecordTrail'
import { listQueryOf } from '@porotutu/react/paging'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createRoute, lazyRouteComponent, useNavigate } from '@tanstack/react-router'
import { actsRoute, grantedRoute } from '@/router/actsRoute'
import { permittedRoute, signedInRoute } from '@/router/root'
import { useSession } from '@/session'
import { MANAGED_USER_ENUMS, MANAGED_USER_SORTABLE, raisesInto } from '../vocabulary'
import { UsersList } from './components/List'
import {
  type ManagedUser,
  membershipsQuery,
  userHistoryQuery,
  userQuery,
  usersQuery,
} from './queries'
import { realmOf } from './realm'

const userSearch = (raw: Record<string, unknown>) =>
  listQueryOf(raw, MANAGED_USER_SORTABLE, [], [], MANAGED_USER_ENUMS)

type UserSearch = ReturnType<typeof userSearch>

const historySearch = historySearchOf(userSearch)

export const userTrail = (
  realm: string,
  id: string,
  search: UserSearch,
  { writable }: Writable,
) => {
  const params = { realm, id }

  return {
    root: { to: '/users/$realm', params: { realm }, search },
    show: { to: '/users/$realm/$id', params, search },
    edit: writable ? { to: '/users/$realm/$id/edit', params, search } : undefined,
    history: { to: '/users/$realm/$id/history', params, search },
  }
}

export const usersRoute = createRoute({
  getParentRoute: () => signedInRoute,
  beforeLoad: permittedRoute('users'),
  path: '/users/$realm',
  validateSearch: userSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ context, params, deps }) =>
    context.queryClient.query(cached(usersQuery({ ...deps, realm: realmOf(params.realm) }))),
  component: UsersPage,
})

function UsersPage() {
  const { realm } = usersRoute.useParams()
  const search = usersRoute.useSearch()
  const navigate = useNavigate({ from: usersRoute.fullPath })
  const { locale } = useSession()
  const query = { ...search, realm: realmOf(realm) }
  const { data: page } = useSuspenseQuery(usersQuery(query))

  return (
    <UsersList
      page={page}
      query={query}
      locale={locale}
      onQuery={(patch) => navigate({ search: (prev) => ({ ...prev, ...patch }) })}
      onReset={() => navigate({ search: {} })}
    />
  )
}

export const newUserRoute = createRoute({
  getParentRoute: () => signedInRoute,
  beforeLoad: grantedRoute('users', ({ realm }: { realm: string }) => raisesInto(realmOf(realm))),
  path: '/users/$realm/new',
  component: lazyRouteComponent(() => import('./pages'), 'NewUserPage'),
})

export const userRoute = createRoute({
  getParentRoute: () => signedInRoute,
  beforeLoad: permittedRoute('users'),
  path: '/users/$realm/$id',
  validateSearch: userSearch,
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.query(cached(userQuery(params.id))),
      context.queryClient.query(cached(membershipsQuery(params.id))),
    ]),
  component: lazyRouteComponent(() => import('./pages'), 'UserPage'),
})

export const editUserRoute = createRoute({
  getParentRoute: () => signedInRoute,
  beforeLoad: actsRoute<ManagedUser>('users', 'edit')(({ id }: { id: string }) => userQuery(id)),
  path: '/users/$realm/$id/edit',
  validateSearch: userSearch,
  loader: ({ context, params }) => context.queryClient.query(cached(userQuery(params.id))),
  component: lazyRouteComponent(() => import('./pages'), 'EditUserPage'),
})

export const userHistoryRoute = createRoute({
  getParentRoute: () => signedInRoute,
  beforeLoad: permittedRoute('users'),
  path: '/users/$realm/$id/history',
  validateSearch: historySearch,
  loaderDeps: historyDeps,
  loader: ({ context, params, deps }) =>
    Promise.all([
      context.queryClient.query(cached(userQuery(params.id))),
      context.queryClient.query(
        cached(userHistoryQuery(params.id, historyWindow(deps.history_offset))),
      ),
    ]),
  component: lazyRouteComponent(() => import('./pages'), 'UserHistoryPage'),
})

export const userRoutes = [usersRoute, newUserRoute, userRoute, editUserRoute, userHistoryRoute]
